const SELECTED_SEARCH_PRODUCT = 'gtmSelectedSearchProduct'

const pdpProductId = (pathname) => {
    const match =
        pathname.match(/(?:^|\/)p\/[^/]+\/([^/]+)\.html\/?$/i) ||
        pathname.match(/(?:^|\/)product\/([^/]+)\/?$/i)
    return match ? decodeURIComponent(match[1]) : null
}

const productIds = (product = {}) =>
    [
        product.productId,
        product.id,
        product.c_variationGroup,
        product.master?.masterId,
        product.representedProduct?.id,
        ...(product.variationGroups || []).map((group) => group.productId),
        ...(product.variants || []).map((variant) => variant.productId),
        // Style-grouped swatches link distinct products through SFCC, not their master ID.
        ...(product.c_associatedVariationAttributes || [])
            .filter((attribute) => attribute.id === 'color')
            .flatMap((attribute) => (attribute.values || []).map((value) => value.associatedPID))
    ].filter(Boolean)

// Use the requested experience, including when smart search falls back to SFCC.
export const getResultsSearchContext = (search) => {
    const params = new URLSearchParams(search)
    return {
        search_type: params.get('smartSearch') === 'true' ? 'smart' : 'default',
        search_state: 'submitted_results',
        ...(params.get('q') && {search_term: params.get('q')})
    }
}

export const rememberSearchProduct = (product, context) => {
    try {
        if (context) {
            window.sessionStorage.setItem(
                SELECTED_SEARCH_PRODUCT,
                JSON.stringify({
                    ids: productIds(product),
                    context,
                    sourcePathname: window.location.pathname
                })
            )
        } else {
            window.sessionStorage.removeItem(SELECTED_SEARCH_PRODUCT)
        }
    } catch {
        // Storage may be unavailable; tracking must not interrupt shopping.
    }
}

export const clearSearchProductOnNavigation = (pathname) => {
    try {
        const stored = JSON.parse(window.sessionStorage.getItem(SELECTED_SEARCH_PRODUCT))
        if (!stored) return
        // A different PDP path can be another colour of the same product.
        // Keep context until the loaded product can be matched by identity.
        if (!pdpProductId(pathname) && (stored.pathname || stored.sourcePathname !== pathname)) {
            window.sessionStorage.removeItem(SELECTED_SEARCH_PRODUCT)
        }
    } catch {
        // Storage may be unavailable.
    }
}

export const getProductSearchContext = (product, interactionLocation) => {
    if (typeof window === 'undefined') return null
    const {pathname, search} = window.location
    if (
        interactionLocation === 'search_process' &&
        (/(?:^|\/)search(?:\/|$)/i.test(pathname) || new URLSearchParams(search).has('q'))
    ) {
        return getResultsSearchContext(search)
    }
    try {
        const routeProductId = pdpProductId(pathname)
        if (!routeProductId) return null
        const stored = JSON.parse(window.sessionStorage.getItem(SELECTED_SEARCH_PRODUCT))
        if (!stored) return null
        const ids = productIds(product)
        // Ignore retained data from the previous route while the new PDP loads.
        if (!ids.includes(routeProductId)) return null
        if (!ids.some((id) => stored.ids.includes(id))) {
            window.sessionStorage.removeItem(SELECTED_SEARCH_PRODUCT)
            return null
        }
        stored.ids = [...new Set([...stored.ids, ...ids])]
        stored.pathname = pathname
        window.sessionStorage.setItem(SELECTED_SEARCH_PRODUCT, JSON.stringify(stored))
        return stored.context
    } catch {
        return null
    }
}

export const getSearchItemAttributes = (product, interactionLocation) => {
    const context = getProductSearchContext(product, interactionLocation)
    return context
        ? {
              c_itemAddLocation: 'search_process',
              c_itemSearchType: context.search_type,
              ...(context.search_term && {c_itemSearchTerm: context.search_term})
          }
        : {}
}
